import { useMemo } from 'react';
import * as styles from './styles.css';
import { useAtomValue } from 'jotai/react';
import { IComponent, IFrame } from '../../interfaces';
import atoms from '../../atoms';
import Submit from '../submit';
import LoadingSpinner from '../skeletons';
import Checkbox from '../common/checkbox';
import Input from '../common/input';
import MultiSelect from '../common/multi-select';
import TreeSelect from '../common/treeselect';
import SingleSelect from '../common/single-select';
import RadioButton from '../common/radio-button';
import Text from '../common/text';
import ContactInfo from '../contact-info';
import Education from '../education';
import Availability from '../availability';
import Review from '../review';

const QuestionTypes = {
  checkbox: Checkbox,
  input: Input,
  multiselect: MultiSelect,
  treeselect: TreeSelect,
  singleselect: SingleSelect,
  radiobutton: RadioButton,
  text: Text,
  information: ContactInfo,
  education: Education,
  availability: Availability,
  review: Review,
};

const getComponent = (type) => QuestionTypes[type];

const ComponentParser = ({ id }: { id?: string }) => {
  const { frames } = useAtomValue<IFrame>(atoms.frame.data);
  const component_list = useAtomValue<IComponent[]>(atoms.component.list);

  const computedFrames = useMemo(() => {
    if (!frames || frames.length === 0) {
      return [];
    }

    const validFrames = frames
      .map(({ id: frameId, components, flex }, frameIndex) => {
        const children = components
          .map(({ id: componentId }) => {
            const component = component_list.find(
              ({ id: component_id }) => component_id === componentId,
            );
            if (!component) return null;
            const { type, show } = component;
            if (!type || !show) return null;
            const Component = getComponent(type);
            if (Component) {
              return (
                <div
                  key={`${componentId}-${type}`}
                  className={frameIndex !== 0 ? styles.margin_bottom : ''}
                >
                  <Component id={componentId} />
                </div>
              );
            }
            return <div key={componentId}>Component {type} not implemented</div>;
          })
          .filter(Boolean);

        // Only return frame if it has valid children
        if (children.length === 0) return null;

        const flex_class = flex === 'row' ? styles.frame_container : '';
        const frameKey = frameId || `frame-${frameIndex}`;
        return (
          <div key={frameKey} className={flex_class}>
            {children}
          </div>
        );
      })
      .filter(Boolean);

    return validFrames;
  }, [frames, component_list]);

  // Show loading spinner only when transitioning between frames (not during API calls)
  if (!computedFrames || computedFrames.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <div className={styles.component_parser}>{computedFrames}</div>
      <Submit id={id} />
    </>
  );
};

export default ComponentParser;
